/**
 * Job at a glance: the new job page's header (job page v3; mockup Corbin
 * approved 2026-10-09, https://claude.ai/artifact/K5KvuVFK5DeKrwfcBAnPTg).
 *
 * One dark band that answers five questions without reading:
 *   What    the type word (from the job type), the #, the status chip
 *   Where   customer (link), site (link), address · city · water authority
 *   Who     the tech (opens Visit), when, the helper (+ Add helper)
 *   Devices passed / failed / not tested as big numbers, counting DEVICES;
 *           reports that don't reconcile get an orange flag that opens the fix
 *   WO      mapped or not, with Open mapper
 * then Money and Paperwork lines, the steps (words with dates), and the pins.
 *
 * It replaces the breadcrumb row, the title block, the vitals, the lineage
 * chips, JobActionBar's light row and StageStrip, ONLY with the new-page
 * switch on. Everything those offered is still here (`GLANCE_REACH` and its
 * test keep it that way); the classic page is untouched.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileEdit, Pencil } from "lucide-react";
import { BackLink } from "@/components/back-link";
import { cn } from "@/lib/utils";
import type { FinishLine } from "@/lib/finish-line";
import type { JobStage, StageStrip } from "../jobDetailHelpers";
import { JobActionBar } from "./job-action-bar";
import type { JobAction, JobActionId } from "./jobActions";
import { MoveJobLink } from "@/components/move-job-customer";
import {
    glanceDevices,
    glanceMoney,
    glancePaperwork,
    glanceStatusWord,
    glanceSteps,
    glanceTypeWord,
    glanceWorkOrder,
    initials,
    stepLine,
    whenWords,
    type DeviceFocus,
} from "./jobGlanceModel";
import type {
    JobAssetRow,
    JobDetail,
    JobRepairResult,
    JobTestResult,
    LinkedEstimate,
    LinkedInvoice,
    WorkOrderMappingInfo,
} from "./types";

/**
 * Everything the v2 header (breadcrumb row, title block, JobActionBar,
 * StageStrip) let you reach, by the test id it has here. `job-glance.test.tsx`
 * renders the header and fails if any one of these is missing.
 */
export const GLANCE_REACH = {
    back: "glance-back",
    "edit-title": "glance-edit-title",
    "copy-number": "glance-number",
    status: "glance-status",
    "open-customer": "glance-customer",
    "open-site": "glance-site",
    "site-name": "header-site-name",
    "tech-and-date": "header-schedule-meta",
    "add-helper": "glance-add-helper",
    devices: "glance-devices",
    "work-order": "glance-wo",
    money: "glance-money",
    paperwork: "glance-paperwork",
    steps: "glance-steps",
    "move-to": "move-to",
    pins: "pins-label",
    "more-for-this-job": "more-for-this-job",
    "classic-page": "page-version-classic",
} as const;

const BAND = "bg-[#22211F] text-[#F3F0E9] dark:bg-[#1F1E1C]";
const LINE = "border-[#3B3A36]";
const PANEL = "bg-[#2B2A27] dark:bg-[#282724]";
const MUTE = "text-[#A8A398]";
const K = "text-[12px] font-bold uppercase tracking-[0.12em] text-[#A8A398]";
const FOCUS_RING =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A24B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#22211F]";
const ACT = "#F28A22";

export interface JobGlanceProps {
    job: JobDetail;
    assets: JobAssetRow[];
    testResults: JobTestResult[];
    repairResults: JobRepairResult[];
    invoices: LinkedInvoice[];
    estimates: LinkedEstimate[];
    workOrder: WorkOrderMappingInfo | null | undefined;
    finishLine: FinishLine | null;
    postVisit: boolean;
    strip: StageStrip;
    onStageClick: (key: JobStage["key"]) => void;
    /** The job actions list and pins (the same JobActionBar the v2 page had). */
    actions: JobAction[];
    pins: JobActionId[];
    pinsAreOfficeDefault: boolean;
    onResetPins: () => void;
    onHold: (reason: string) => void;
    holdRequest?: number;
    onMoreForThisJob: () => void;
    /** Back to the classic job page (the per-user switch). */
    onClassic: () => void;
    onEditTitle: () => void;
    /** The title editor, while editing (Save / Cancel live in detail.tsx). */
    titleEditor?: ReactNode;
    onCopyNumber: () => void;
    /** The tech and the date open Visit (date or tech change). */
    onSchedule: () => void;
    onAddHelper: () => void;
    /** A device tile or flag: open the Devices list at that group. */
    onDevices: (focus: DeviceFocus) => void;
    onPaperwork: () => void;
    /** The vendor WO's intake classification chip (the old vitals strip). */
    classification?: ReactNode;
    /** The customer's budget chip. */
    budget?: ReactNode;
    /** Time on site, arrival lag, last reached us. */
    timing?: ReactNode;
}

export function JobGlance(p: JobGlanceProps) {
    const { job } = p;
    const typeWord = glanceTypeWord(job);
    const number = job.display_number || job.id.slice(0, 8);
    const dev = glanceDevices(job, p.assets, p.testResults, p.repairResults, p.postVisit);
    const wo = glanceWorkOrder(job, p.workOrder);
    const money = glanceMoney(job, p.invoices, p.estimates);
    const paper = glancePaperwork(p.finishLine);
    const steps = glanceSteps(p.strip);
    const mini = stepLine(steps);
    const helpers = (job.crew ?? []).filter((c) => c.role === "helper" && c.name);
    const water = job.water_provider_display_name || job.water_provider_name;
    const address = job.site_address_line ?? job.location_address ?? null;
    const terminal = p.strip.terminal;

    return (
        <section
            className={cn("@container overflow-hidden rounded-xl shadow-sm", BAND)}
            aria-label={`${typeWord} #${number} at a glance`}
            data-testid="job-glance"
        >
            <div className="flex flex-col gap-3.5 px-3.5 pb-3.5 pt-3 @3xl:px-5 @3xl:pb-4 @3xl:pt-3.5">
                {/* Top: back, and the page-version switch, in plain sight. */}
                <div className={cn("flex items-center justify-between gap-3 text-[14px]", MUTE)}>
                    <span data-testid="glance-back">
                        <BackLink
                            fallback="/jobs"
                            fallbackLabel="Jobs"
                            className={cn(
                                "inline-flex items-center gap-1 rounded-sm text-[14px] text-[#A8A398] hover:text-[#F3F0E9]",
                                FOCUS_RING,
                            )}
                            icon={<ArrowLeft className="h-3.5 w-3.5" />}
                        />
                    </span>
                    <div
                        role="group"
                        aria-label="Page version"
                        className={cn("inline-flex rounded-full border p-0.5", LINE, PANEL)}
                        data-testid="page-version-toggle"
                    >
                        <button
                            type="button"
                            aria-pressed={false}
                            onClick={p.onClassic}
                            className={cn(
                                "rounded-full px-2.5 py-1 text-[12.5px] text-[#A8A398] hover:text-[#F3F0E9] @3xl:px-3 @3xl:text-[13px]",
                                FOCUS_RING,
                            )}
                            data-testid="page-version-classic"
                        >
                            Classic page
                        </button>
                        <button
                            type="button"
                            aria-pressed
                            className={cn(
                                "rounded-full bg-[#C9A24B] px-2.5 py-1 text-[12.5px] font-bold text-[#1b1a17] @3xl:px-3 @3xl:text-[13px]",
                                FOCUS_RING,
                            )}
                            data-testid="page-version-new"
                        >
                            New page
                        </button>
                    </div>
                </div>

                {/* What */}
                <div className="flex flex-wrap items-center gap-x-3.5 gap-y-1.5">
                    <h1
                        className="m-0 text-[30px] font-black leading-none tracking-tight @3xl:text-[38px]"
                        data-testid="glance-type"
                    >
                        {typeWord}
                    </h1>
                    <button
                        type="button"
                        className={cn(
                            "rounded-sm font-mono text-[18px] font-semibold text-[#C9A24B] hover:underline @3xl:text-[22px]",
                            FOCUS_RING,
                        )}
                        title="Click to copy job number"
                        onClick={p.onCopyNumber}
                        data-testid="glance-number"
                    >
                        #{number}
                    </button>
                    <span
                        className="inline-flex items-center rounded-full bg-[#F3F0E9] px-3 py-0.5 text-[14px] font-bold leading-snug text-[#1b1a17]"
                        data-testid="glance-status"
                    >
                        {glanceStatusWord(job.status)}
                    </span>
                </div>
                {p.titleEditor ?? (
                    <div className={cn("-mt-1.5 flex items-start gap-2 text-[14px]", MUTE)}>
                        <span className="min-w-0" data-testid="glance-title">
                            {job.title}
                        </span>
                        <button
                            type="button"
                            className={cn("mt-0.5 shrink-0 rounded-sm hover:text-[#F3F0E9]", FOCUS_RING)}
                            onClick={p.onEditTitle}
                            aria-label="Edit title"
                            data-testid="glance-edit-title"
                        >
                            <Pencil className="h-3.5 w-3.5" />
                        </button>
                    </div>
                )}

                {/* Where · Who · Devices */}
                <div className={cn("grid grid-cols-1 border-t @3xl:grid-cols-[1.15fr_1fr_1.35fr] @3xl:border-b", LINE)}>
                    <div className="flex min-w-0 flex-col gap-1 py-3 @3xl:py-3.5 @3xl:pr-[18px]">
                        <span className={K}>Where</span>
                        {job.customer_id ? (
                            <Link
                                to={`/customers/${job.customer_id}`}
                                className={cn(
                                    "rounded-sm text-[19px] font-bold leading-tight underline decoration-[#3B3A36] underline-offset-4 hover:decoration-[#C9A24B] @3xl:text-[21px]",
                                    FOCUS_RING,
                                )}
                                data-testid="glance-customer"
                            >
                                {job.company_name || "Customer"}
                            </Link>
                        ) : (
                            <span className="text-[19px] font-bold" data-testid="glance-customer">
                                No customer
                            </span>
                        )}
                        {/* #1897 — office fix for a job built under the wrong customer. */}
                        <MoveJobLink jobId={job.id} className="-ml-1.5 self-start" />
                        {(job.site_name || job.site_city) &&
                            (job.site_id ? (
                                <Link
                                    to={`/sites/${job.site_id}`}
                                    className={cn(
                                        "rounded-sm text-[17px] font-bold leading-snug hover:underline",
                                        FOCUS_RING,
                                    )}
                                    data-testid="glance-site"
                                >
                                    <span data-testid="header-site-name">
                                        {[job.site_name, job.site_city].filter(Boolean).join(" · ")}
                                    </span>
                                </Link>
                            ) : (
                                <span className="text-[17px] font-bold" data-testid="glance-site">
                                    <span data-testid="header-site-name">
                                        {[job.site_name, job.site_city].filter(Boolean).join(" · ")}
                                    </span>
                                </span>
                            ))}
                        {!job.site_name && !job.site_city && job.site_id && (
                            <Link
                                to={`/sites/${job.site_id}`}
                                className={cn("rounded-sm text-[17px] font-bold hover:underline", FOCUS_RING)}
                                data-testid="glance-site"
                            >
                                Open the site
                            </Link>
                        )}
                        <span className={cn("text-[14px]", MUTE)}>
                            {[address, water ? `Water: ${water}` : null].filter(Boolean).join(" · ") ||
                                "No address on file"}
                        </span>
                        <GlanceFacts job={job} classification={p.classification} budget={p.budget} />
                    </div>

                    <div
                        className={cn(
                            "flex min-w-0 flex-col gap-1 border-t py-3 @3xl:border-l @3xl:border-t-0 @3xl:px-[18px] @3xl:py-3.5",
                            LINE,
                        )}
                    >
                        <span className={K}>Who</span>
                        <button
                            type="button"
                            className={cn("flex items-center gap-3 rounded-lg text-left", FOCUS_RING)}
                            onClick={p.onSchedule}
                            title="Change the tech, crew or appointment"
                            data-testid="header-schedule-meta"
                        >
                            <span
                                className="grid h-12 w-12 flex-none place-items-center rounded-full bg-[#C9A24B] text-[17px] font-bold text-[#1b1a17]"
                                aria-hidden
                            >
                                {job.assigned_to_name ? initials(job.assigned_to_name) : "?"}
                            </span>
                            <span className="min-w-0">
                                <span className="block text-[17px] font-bold leading-snug">
                                    {job.assigned_to_name || "No tech yet"}
                                </span>
                                <span className={cn("block text-[14px]", MUTE)}>{whenWords(job, p.postVisit)}</span>
                            </span>
                        </button>
                        <div className={cn("mt-1.5 flex flex-wrap items-center gap-2 text-[14px]", MUTE)}>
                            <span
                                className={cn(
                                    "grid h-[30px] w-[30px] flex-none place-items-center rounded-full border border-dashed text-[12px] font-bold",
                                    LINE,
                                    PANEL,
                                )}
                                aria-hidden
                            >
                                {helpers.length > 0 ? initials(helpers[0]!.name) : "+"}
                            </span>
                            {helpers.length > 0 ? (
                                <span className="text-[#F3F0E9]">
                                    {helpers.map((h) => h.name).join(", ")}
                                    {" · "}
                                </span>
                            ) : (
                                <span>No helper · </span>
                            )}
                            <button
                                type="button"
                                className={cn("rounded-sm font-bold text-[#C9A24B] hover:underline", FOCUS_RING)}
                                onClick={p.onAddHelper}
                                data-testid="glance-add-helper"
                            >
                                {helpers.length > 0 ? "Add another" : "Add"}
                            </button>
                        </div>
                    </div>

                    <div
                        className={cn(
                            "flex min-w-0 flex-col gap-1 border-t py-3 @3xl:border-l @3xl:border-t-0 @3xl:py-3.5 @3xl:pl-[18px]",
                            LINE,
                        )}
                        data-testid="glance-devices"
                    >
                        <span className={K}>Devices · {dev.onJob} on this job</span>
                        <div className="grid grid-cols-3 gap-2">
                            <Tile
                                n={dev.passed}
                                word="Passed"
                                mark="✓"
                                color="#3DBA6F"
                                onClick={() => p.onDevices("pass")}
                                testId="glance-tile-pass"
                            />
                            <Tile
                                n={dev.failed}
                                word="Failed"
                                mark="✕"
                                color="#EF4F55"
                                onClick={() => p.onDevices("fail")}
                                testId="glance-tile-fail"
                            />
                            <Tile
                                n={dev.notTested}
                                word={dev.notTestedWord}
                                mark="–"
                                color="#8D887E"
                                onClick={() => p.onDevices("none")}
                                testId="glance-tile-none"
                            />
                        </div>
                        <div className={cn("mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-[14px]", MUTE)}>
                            <span data-testid="glance-tested">
                                {dev.tested} of {dev.onJob} tested
                            </span>
                            {dev.flags.map((f) => (
                                <button
                                    key={f.kind}
                                    type="button"
                                    className={cn("rounded-sm text-left font-bold hover:underline", FOCUS_RING)}
                                    style={{ color: ACT }}
                                    onClick={() => p.onDevices(f.focus ?? "none")}
                                    data-testid={`glance-flag-${f.kind}`}
                                >
                                    ! {f.text}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Work order · Money · Paperwork */}
                <div className="grid grid-cols-1 gap-2.5 @3xl:grid-cols-3">
                    <WorkOrderLine wo={wo} />
                    <Line k="Money" need={money.some((m) => m.act)} testId="glance-money">
                        {money.length === 0 ? (
                            <span className={cn("text-[16px] font-bold", MUTE)}>Nothing billed yet</span>
                        ) : (
                            money.map((m) => (
                                <Link
                                    key={m.key}
                                    to={m.href}
                                    className={cn(
                                        "flex flex-wrap items-center gap-x-2.5 gap-y-0.5 rounded-sm hover:underline",
                                        FOCUS_RING,
                                    )}
                                >
                                    {m.act && <Dot />}
                                    <span className="text-[16px] font-bold">
                                        {m.what}
                                        {m.number && <span className="font-mono font-semibold"> #{m.number}</span>}
                                        {m.amount && ` · ${m.amount}`}
                                    </span>
                                    <span className={cn("text-[14px]", MUTE)}>{m.state}</span>
                                </Link>
                            ))
                        )}
                    </Line>
                    <Line k="Paperwork" need={!!paper?.act} testId="glance-paperwork">
                        <button
                            type="button"
                            className={cn("flex flex-col items-start gap-1 rounded-sm text-left", FOCUS_RING)}
                            onClick={p.onPaperwork}
                        >
                            <span className="flex flex-wrap items-center gap-2.5">
                                {paper?.act && <Dot />}
                                {paper && !paper.done && !paper.later && (
                                    <span className={cn("text-[14px]", MUTE)}>Next:</span>
                                )}
                                <span
                                    className={cn(
                                        "text-[16px] font-bold",
                                        paper?.done && "text-[#3DBA6F]",
                                        paper?.later && "text-[#A8A398]",
                                    )}
                                >
                                    {paper
                                        ? paper.done && paper.main !== "No paperwork for this job"
                                            ? `✓ ${paper.main}`
                                            : paper.main
                                        : "Checking the paperwork…"}
                                </span>
                            </span>
                            {paper?.sub && <span className={cn("text-[14px]", MUTE)}>{paper.sub}</span>}
                        </button>
                    </Line>
                </div>

                {/* Steps: words with dates; one line on a phone. */}
                <div
                    className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5"
                    data-testid="glance-steps"
                >
                    <ol
                        className="m-0 hidden list-none items-center overflow-x-auto p-0 @3xl:flex"
                        aria-label="Job steps"
                    >
                        {steps.map((s, i) => (
                            <li key={s.key} className="flex items-center whitespace-nowrap">
                                {i > 0 && (
                                    <span
                                        className={cn(
                                            "mx-2 h-0.5 w-[22px]",
                                            steps[i - 1]!.state === "done" ? "bg-[#C9A24B]" : "bg-[#3B3A36]",
                                        )}
                                        aria-hidden
                                    />
                                )}
                                <button
                                    type="button"
                                    onClick={() => p.onStageClick(s.key)}
                                    className={cn(
                                        "flex items-center gap-2 rounded-sm text-[14px]",
                                        s.state === "todo" ? MUTE : "text-[#F3F0E9]",
                                        s.state === "now" && "font-bold",
                                        FOCUS_RING,
                                    )}
                                    aria-current={s.state === "now" ? "step" : undefined}
                                    data-testid={`glance-step-${s.key}`}
                                >
                                    <span
                                        className={cn(
                                            "grid h-5 w-5 place-items-center rounded-full border-2 text-[12px] font-bold",
                                            s.state === "done"
                                                ? "border-[#C9A24B] bg-[#C9A24B] text-[#1b1a17]"
                                                : s.state === "now"
                                                  ? "border-[#C9A24B] shadow-[0_0_0_3px_rgba(201,162,75,0.3)]"
                                                  : "border-[#3B3A36]",
                                        )}
                                        aria-hidden
                                    >
                                        {s.state === "done" ? (
                                            "✓"
                                        ) : s.state === "now" ? (
                                            <span className="h-2 w-2 rounded-full bg-[#C9A24B]" />
                                        ) : null}
                                    </span>
                                    {s.label}
                                    {s.when && s.state !== "now" && (
                                        <span className={cn("text-[12px] font-normal", MUTE)}>{s.when}</span>
                                    )}
                                </button>
                            </li>
                        ))}
                        {terminal && (
                            <li
                                className="ml-3 whitespace-nowrap rounded-full border-[1.5px] px-2.5 py-0.5 text-[13px] font-bold"
                                style={{ borderColor: ACT, color: ACT }}
                            >
                                {terminal === "held"
                                    ? "On hold"
                                    : terminal === "cancelled"
                                      ? "Cancelled"
                                      : "Cannot complete"}
                            </li>
                        )}
                    </ol>
                    {mini && (
                        <p className="m-0 text-[15px] @3xl:hidden" data-testid="glance-step-line">
                            {mini.split(": ")[0]}:{" "}
                            <b className="text-[#C9A24B]">{mini.split(": ").slice(1).join(": ").split(" · ")[0]}</b>
                            {mini.includes(" · ") && ` · ${mini.split(" · ").slice(1).join(" · ")}`}
                            {terminal &&
                                ` · ${terminal === "held" ? "On hold" : terminal === "cancelled" ? "Cancelled" : "Cannot complete"}`}
                        </p>
                    )}
                    {p.timing && (
                        <div
                            className={cn("flex flex-wrap items-center gap-4 text-[13px] [&_*]:!text-[#A8A398]", MUTE)}
                        >
                            {p.timing}
                        </div>
                    )}
                </div>

                <JobActionBar
                    tone="band"
                    actions={p.actions}
                    pins={p.pins}
                    pinsAreOfficeDefault={p.pinsAreOfficeDefault}
                    onResetPins={p.onResetPins}
                    onHold={p.onHold}
                    holdRequest={p.holdRequest}
                    onMoreForThisJob={p.onMoreForThisJob}
                />
            </div>
        </section>
    );
}

/**
 * The old vitals strip, on the band: PO, the WO's classification, the
 * location phone (which line it is), the water account, and the tax chip.
 */
function GlanceFacts({
    job,
    classification,
    budget,
}: {
    job: JobDetail;
    classification?: ReactNode;
    budget?: ReactNode;
}) {
    const po = job.customer_po?.trim();
    const phone = job.site_contact_phone?.trim() || job.site_phone?.trim() || job.customer_phone?.trim();
    const phoneWho = job.site_contact_phone?.trim()
        ? "site contact"
        : job.site_phone?.trim()
          ? "site line"
          : "customer";
    const acct = job.site_water_account_number?.trim();
    const hasProvider = !!(job.water_provider_id || job.water_provider_name);
    const tax = job.customer_tax_status;
    const warn = { color: ACT };
    if (!po && !phone && !hasProvider && !tax && !classification && !budget) return null;
    return (
        <div
            className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[#D6D1C6]"
            data-testid="glance-facts"
        >
            {po && <span className="tabular-nums">PO {po}</span>}
            {classification}
            {phone && (
                <a
                    href={`tel:${phone}`}
                    className={cn("rounded-sm tabular-nums hover:text-[#F3F0E9] hover:underline", FOCUS_RING)}
                    title="Location contact phone"
                >
                    {phone} <span className={MUTE}>({phoneWho})</span>
                </a>
            )}
            {hasProvider &&
                (!job.water_provider_is_portal ? (
                    <span className={MUTE} title="This city files on paper or by mail, so no account number applies">
                        No acct · paper city
                    </span>
                ) : acct ? (
                    <span className="tabular-nums" title="Water provider account #">
                        Acct {acct}
                    </span>
                ) : (
                    <span
                        className="font-bold"
                        style={warn}
                        title="Portal city with no water account # on the site. Add it before filing."
                    >
                        Water acct missing
                    </span>
                ))}
            {tax === "exempt" ? (
                <span title={job.customer_tax_exempt_reason || "Tax exempt"}>Tax exempt</span>
            ) : tax === "taxable" ? (
                <span title="Sales tax applies on this customer's invoices">Taxable</span>
            ) : tax === "unknown" ? (
                <span
                    className="font-bold"
                    style={warn}
                    title="Customer tax status not set. Invoice sends hold until it's decided."
                >
                    Tax status?
                </span>
            ) : null}
            {budget}
        </div>
    );
}

function Tile({
    n,
    word,
    mark,
    color,
    onClick,
    testId,
}: {
    n: number;
    word: string;
    mark: string;
    color: string;
    onClick: () => void;
    testId: string;
}) {
    const zero = n === 0;
    const c = zero ? "#3B3A36" : color;
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                "flex min-w-0 flex-col rounded-[10px] border border-t-4 px-2.5 pb-2.5 pt-2 text-left @md:px-3",
                LINE,
                PANEL,
                FOCUS_RING,
            )}
            style={{ borderTopColor: c }}
            aria-label={`${n} ${word.toLowerCase()}`}
            data-testid={testId}
        >
            <span
                className="text-[38px] font-black leading-none tabular-nums @3xl:text-[44px]"
                style={{ color: zero ? "#A8A398" : color }}
            >
                {n}
            </span>
            <span className="flex items-baseline gap-1 text-[13px] font-bold leading-tight @md:text-[14px] @3xl:text-[15px]">
                <span aria-hidden>{mark}</span> {word}
            </span>
        </button>
    );
}

function Dot() {
    return (
        <span
            role="img"
            aria-label="Needs us"
            className="inline-block h-[9px] w-[9px] flex-none rounded-full"
            style={{ background: ACT }}
        />
    );
}

function Line({ k, need, testId, children }: { k: string; need: boolean; testId: string; children: ReactNode }) {
    return (
        <div
            className={cn("flex min-w-0 flex-col gap-1.5 rounded-[10px] border px-3.5 py-2.5", PANEL, need ? "" : LINE)}
            style={need ? { borderColor: ACT } : undefined}
            data-testid={testId}
            data-needs-us={need || undefined}
        >
            <span className={K}>{k}</span>
            {children}
        </div>
    );
}

function WorkOrderLine({ wo }: { wo: ReturnType<typeof glanceWorkOrder> }) {
    if (wo.kind === "none") {
        return (
            <Line k="Work order" need={wo.required} testId="glance-wo">
                {wo.required ? (
                    <span className="flex items-center gap-2.5">
                        <Dot />
                        <span className="text-[16px] font-bold">Customer needs one · none on file</span>
                    </span>
                ) : (
                    <>
                        <span className={cn("text-[16px] font-bold", MUTE)}>No work order</span>
                        <span className={cn("text-[14px]", MUTE)}>This customer doesn&apos;t use one</span>
                    </>
                )}
            </Line>
        );
    }
    const ok = wo.mapped;
    return (
        <Line k="Work order" need={!ok && !!wo.mapperHref} testId="glance-wo">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                <span className="text-[16px] font-bold">
                    {wo.number ? (
                        <>
                            WO <span className="font-mono font-semibold">{wo.number}</span>
                        </>
                    ) : (
                        "Work order"
                    )}
                </span>
                {wo.mapperHref &&
                    (ok ? (
                        <span
                            className="inline-flex items-center rounded-full border-[1.5px] px-3 py-0.5 text-[14px] font-bold"
                            style={{ borderColor: "#3DBA6F", color: "#3DBA6F" }}
                            data-testid="glance-wo-mapped"
                        >
                            ✓ Mapped
                        </span>
                    ) : (
                        <span
                            className="inline-flex items-center rounded-full border-[1.5px] px-3 py-0.5 text-[14px] font-bold"
                            style={{ borderColor: ACT, color: ACT }}
                            data-testid="glance-wo-needs"
                        >
                            ! {wo.hasTemplate ? "Needs mapping" : "No template yet"}
                        </span>
                    ))}
            </div>
            {wo.mapperHref ? (
                <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                    <Link
                        to={wo.mapperHref}
                        className={cn(
                            "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[14px] font-bold",
                            ok ? "border-[#3B3A36] text-[#F3F0E9] hover:border-[#C9A24B]" : "text-[#1b1a17]",
                            FOCUS_RING,
                        )}
                        style={ok ? undefined : { background: ACT, borderColor: ACT }}
                        data-testid="glance-open-mapper"
                    >
                        <FileEdit className="h-4 w-4" aria-hidden />
                        {wo.hasTemplate ? "Open mapper" : "Set up the WO"}
                    </Link>
                    <span className={cn("text-[14px]", MUTE)}>
                        {ok
                            ? `${wo.fields} field${wo.fields === 1 ? "" : "s"} placed${wo.template ? ` · ${wo.template}` : ""}`
                            : "Check where values stamp before it goes out"}
                    </span>
                </div>
            ) : (
                <span className={cn("text-[14px]", MUTE)}>Came in without a WO intake, so there&apos;s no mapper</span>
            )}
        </Line>
    );
}
